import { Injectable, OnDestroy } from '@angular/core';
import hotkeys from 'hotkeys-js';

export type HotkeyCallback = (event: KeyboardEvent, combo: string) => boolean | void;

export class Hotkey {
  public allowIn: string[] = [];
  public description: string = '';
  public action: string = 'keydown';

  constructor(
    public combo: string | string[],
    public callback: HotkeyCallback,
    allowIn?: string[],
    description?: string,
    action?: string,
  ) {
    if (allowIn) { this.allowIn = allowIn; }
    if (description) { this.description = description; }
    if (action) { this.action = action; }
  }

  public formatted(): string[] {
    return Array.isArray(this.combo) ? this.combo : [this.combo];
  }
}

const FORM_FIELD_TAGS = ['INPUT', 'SELECT', 'TEXTAREA'];

@Injectable({ providedIn: 'root' })
export class HotkeysService implements OnDestroy {
  public hotkeys: Hotkey[] = [];

  constructor() {
    // Match angular2-hotkeys: skip shortcuts when focus is on a form field unless the
    // Hotkey explicitly opts in via `allowIn`. The per-hotkey check happens in `add()`;
    // returning true here lets every event reach our callback so we can decide there.
    hotkeys.filter = () => true;
  }

  public add(hotkey: Hotkey | Hotkey[]): Hotkey | Hotkey[] {
    const list = Array.isArray(hotkey) ? hotkey : [hotkey];
    list.forEach((h) => {
      this.hotkeys.push(h);
      h.formatted().forEach((combo) => {
        const normalized = this._normalize(combo);
        hotkeys(normalized, (event, handler) => {
          if (this._shouldSkipInFormField(event, h)) {
            return;
          }
          const result = h.callback(event, handler.key);
          if (result === false) {
            event.preventDefault();
          }
          return result as boolean | void;
        });
      });
    });
    return hotkey;
  }

  private _shouldSkipInFormField(event: KeyboardEvent, hotkey: Hotkey): boolean {
    const target = (event.target || (event as any).srcElement) as HTMLElement | null;
    if (!target || !target.tagName) {
      return false;
    }
    const tagName = target.tagName.toUpperCase();
    if (!FORM_FIELD_TAGS.includes(tagName)) {
      return false;
    }
    const allowed = hotkey.allowIn.map((t) => t.toUpperCase());
    return !allowed.includes(tagName);
  }

  public remove(hotkey?: Hotkey | Hotkey[]): Hotkey | Hotkey[] | null {
    if (!hotkey) {
      this.hotkeys.forEach((h) => h.formatted().forEach((combo) => hotkeys.unbind(this._normalize(combo))));
      this.hotkeys = [];
      return null;
    }
    const list = Array.isArray(hotkey) ? hotkey : [hotkey];
    list.forEach((h) => {
      const idx = this.hotkeys.indexOf(h);
      if (idx !== -1) { this.hotkeys.splice(idx, 1); }
      h.formatted().forEach((combo) => hotkeys.unbind(this._normalize(combo)));
    });
    return hotkey;
  }

  public get(combo: string): Hotkey | null {
    return this.hotkeys.find((h) => h.formatted().includes(combo)) ?? null;
  }

  public ngOnDestroy(): void {
    this.remove();
  }

  // hotkeys-js uses the same `mod+key` syntax as Mousetrap (which angular2-hotkeys wraps),
  // so most combos pass through unchanged. Keep this hook in case translations are needed later.
  private _normalize(combo: string): string {
    return combo;
  }
}
